import type { Point } from "~/lib/svgEditor/zoom";

/** A 2D affine matrix, as SVG's `matrix(a b c d e f)`. */
export type Matrix = {
    a: number;
    b: number;
    c: number;
    d: number;
    e: number;
    f: number;
};

export const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** The point `matrix` maps `point` to. */
export function apply(matrix: Matrix, point: Point): Point {
    return {
        x: matrix.a * point.x + matrix.c * point.y + matrix.e,
        y: matrix.b * point.x + matrix.d * point.y + matrix.f,
    };
}

/** The inverse of `matrix`, if it has one. */
export function invert(matrix: Matrix): Matrix | undefined {
    const { a, b, c, d, e, f } = matrix;
    const determinant = a * d - b * c;

    if (determinant === 0) return;

    return {
        a: d / determinant,
        b: -b / determinant,
        c: -c / determinant,
        d: a / determinant,
        e: (c * f - d * e) / determinant,
        f: (b * e - a * f) / determinant,
    };
}

/** `matrix` as the value of a `transform` attribute, rounded to a millionth. */
export function matrixAttribute(matrix: Matrix): string {
    const values = [matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f]
        .map((value) => String(Number(value.toFixed(6))))
        .join(" ");

    return `matrix(${values})`;
}

/** `first` applied after `second`: the product `first · second`. */
export function multiply(first: Matrix, second: Matrix): Matrix {
    return {
        a: first.a * second.a + first.c * second.b,
        b: first.b * second.a + first.d * second.b,
        c: first.a * second.c + first.c * second.d,
        d: first.b * second.c + first.d * second.d,
        e: first.a * second.e + first.c * second.f + first.e,
        f: first.b * second.e + first.d * second.f + first.f,
    };
}

/** A turn by `angle` radians — clockwise on screen, where y goes down. */
export function rotation(angle: number): Matrix {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
}

/** How much `matrix` scales lengths, taken as uniform: the root of its area factor. */
export function scaleFactor(matrix: Matrix): number {
    const factor = Math.sqrt(
        Math.abs(matrix.a * matrix.d - matrix.b * matrix.c),
    );

    return factor > 0 ? factor : 1;
}

export function scaling(x: number, y: number): Matrix {
    return { a: x, b: 0, c: 0, d: y, e: 0, f: 0 };
}

export function translation(x: number, y: number): Matrix {
    return { a: 1, b: 0, c: 0, d: 1, e: x, f: y };
}
